import { cn } from "@/lib/utils";
import { Play, FileText, HelpCircle, BookMarked, MessageCircle, Lock, Check } from "lucide-react";
import type { AnyValue } from "@/lib/rowTypes";

export type LessonStageType = "video" | "infographic" | "reading" | "mastery" | "decision" | "questions";

interface Props {
  currentStage: LessonStageType;
  availableStages: LessonStageType[];
  onStageChange: (stage: LessonStageType) => void;
  completedStages: Set<LessonStageType>;
}

const STAGE_CONFIG: Record<
  LessonStageType,
  { label: string; shortLabel: string; icon: AnyValue; activeClass: string }
> = {
  // Each stage has its own active holo color so the learner always knows where they are.
  video: {
    label: "Video",
    shortLabel: "Video",
    icon: Play,
    activeClass:
      "border-[hsl(0_85%_65%/0.7)] bg-[hsl(0_85%_65%/0.18)] text-[hsl(0_90%_82%)] shadow-[0_0_18px_-4px_hsl(0_85%_65%/0.75)]",
  },
  infographic: {
    label: "Visual",
    shortLabel: "Visual",
    icon: FileText,
    activeClass:
      "border-[hsl(195_90%_60%/0.7)] bg-[hsl(195_90%_60%/0.18)] text-[hsl(195_95%_82%)] shadow-[0_0_18px_-4px_hsl(195_90%_60%/0.75)]",
  },
  reading: {
    label: "Case Study",
    shortLabel: "Case",
    icon: FileText,
    activeClass:
      "border-[hsl(160_75%_55%/0.7)] bg-[hsl(160_75%_55%/0.18)] text-[hsl(160_85%_80%)] shadow-[0_0_18px_-4px_hsl(160_75%_55%/0.75)]",
  },
  mastery: {
    label: "Insight",
    shortLabel: "Insight",
    icon: BookMarked,
    activeClass:
      "border-[hsl(265_90%_72%/0.7)] bg-[hsl(265_90%_72%/0.18)] text-[hsl(265_95%_85%)] shadow-[0_0_18px_-4px_hsl(265_90%_72%/0.75)]",
  },
  decision: {
    label: "Decide",
    shortLabel: "Decide",
    icon: MessageCircle,
    activeClass:
      "border-[hsl(38_95%_65%/0.7)] bg-[hsl(38_95%_65%/0.18)] text-[hsl(38_95%_82%)] shadow-[0_0_18px_-4px_hsl(38_95%_65%/0.75)]",
  },
  questions: {
    label: "Quiz",
    shortLabel: "Quiz",
    icon: HelpCircle,
    activeClass:
      "border-[hsl(330_85%_65%/0.7)] bg-[hsl(330_85%_65%/0.18)] text-[hsl(330_95%_85%)] shadow-[0_0_18px_-4px_hsl(330_85%_65%/0.75)]",
  },
};

export default function LessonStageNav({ currentStage, availableStages, onStageChange, completedStages }: Props) {
  if (availableStages.length <= 1) return null;

  // Compress labels when there are many stages to avoid horizontal collisions on small screens
  const useShortLabels = availableStages.length >= 5;

  return (
    <div
      className="flex w-full max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden p-1 bg-card/80 backdrop-blur-md rounded-xl border border-border/50 sm:w-auto sm:justify-center"
      style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
    >
      {availableStages.map((stage, idx) => {
        const config = STAGE_CONFIG[stage];
        const Icon = config.icon;
        const isActive = stage === currentStage;
        const isCompleted = completedStages.has(stage);
        // A stage is accessible if it's completed, is the current stage, or all prior stages are completed
        const allPriorCompleted = availableStages.slice(0, idx).every(s => completedStages.has(s));
        const isUnlocked = isCompleted || isActive || allPriorCompleted;

        // Active always wins over completed — revisiting a completed tab returns to its own color.
        return (
          <button
            key={stage}
            onClick={() => isUnlocked && onStageChange(stage)}
            disabled={!isUnlocked}
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-lg px-2.5 text-[11px] font-heading font-medium transition-all duration-200 sm:px-3 sm:text-xs",
              "min-h-[36px] sm:min-h-[32px] border",
              isActive
                ? config.activeClass
                : isCompleted
                ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-300 hover:bg-emerald-400/10"
                : isUnlocked
                ? "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
                : "border-transparent text-muted-foreground/35 cursor-not-allowed opacity-60"
            )}
          >
            {!isUnlocked && !isCompleted && <Lock className="h-3 w-3 shrink-0" />}
            {isCompleted && !isActive ? (
              <Check className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <Icon className="h-3.5 w-3.5 shrink-0" />
            )}
            <span className="whitespace-nowrap">{useShortLabels ? config.shortLabel : config.label}</span>
          </button>
        );
      })}
    </div>
  );
}
